import React, { useEffect, useState } from 'react';
import * as ReactModule from 'react';
import * as ReactDOMModule from 'react-dom';
import * as ReactRouterDomModule from 'react-router-dom';
import * as StyledComponentsModule from 'styled-components';

import { saveServerLogs } from 'UtilityComponents/Util';

const REMOTE_NAME = 'corpCommon';
const EXPOSED_MODULE = './approvalButtons';
const REMOTE_SCRIPT_ID = 'corp-common-approval-buttons-remote-entry';
const PROD_REMOTE_ENTRY = 'https://jsak.mmtcdn.com/mybiz-common/assets/dist/client/remoteEntry.js';
const DEV_REMOTE_ENTRY = 'https://jsak.mmtcdn.com/mybiz-common/remoteEntry.js';

const getRemoteEntryUrl = () =>
	(typeof isDev !== 'undefined' && isDev && false ? DEV_REMOTE_ENTRY : PROD_REMOTE_ENTRY);

let remoteComponentPromise;

const REMOTE_SHARED_SCOPE = {
	'react': {
		'16.14.0': {
			get: () => () => ReactModule,
			loaded: 1,
			from: 'corpReact'
		}
	},
	'react-dom': {
		'16.14.0': {
			get: () => () => ReactDOMModule,
			loaded: 1,
			from: 'corpReact'
		}
	},
	'react-router-dom': {
		'5.2.0': {
			get: () => () => ReactRouterDomModule,
			loaded: 1,
			from: 'corpReact'
		}
	},
	'styled-components': {
		'6.1.8': {
			get: () => () => StyledComponentsModule,
			loaded: 1,
			from: 'corpReact'
		}
	}
};

const appendCacheKey = (url) => {
	const separator = url.includes('?') ? '&' : '?';
	const cacheKey = window.remoteCacheKey || window.commonAppVer || Date.now();
	return `${url}${separator}v=${cacheKey}`;
};

const loadRemoteScript = () => new Promise((resolve, reject) => {
	if (window[REMOTE_NAME]) {
		resolve(window[REMOTE_NAME]);
		return;
	}

	const existingScript = document.getElementById(REMOTE_SCRIPT_ID);
	if (existingScript) {
		existingScript.addEventListener('load', () => resolve(window[REMOTE_NAME]), { once: true });
		existingScript.addEventListener('error', () => reject(new Error('Failed to load corpCommon remote entry')), { once: true });
		return;
	}

	const script = document.createElement('script');
	script.id = REMOTE_SCRIPT_ID;
	script.src = appendCacheKey(getRemoteEntryUrl());
	script.async = true;
	script.crossOrigin = 'anonymous';
	script.onload = () => resolve(window[REMOTE_NAME]);
	script.onerror = () => reject(new Error(`Failed to load corpCommon remote entry from ${script.src}`));
	document.body.appendChild(script);
});

const getRemoteApprovalButtonsComponent = async () => {
	if (!remoteComponentPromise) {
		remoteComponentPromise = loadRemoteScript()
			.then(async (container) => {
				if (!container) {
					throw new Error(`Remote container ${REMOTE_NAME} was not found on window`);
				}

				try {
					await container.init(REMOTE_SHARED_SCOPE);
				} catch (error) {
					saveServerLogs(error && error.stack ? error.stack : error, 'error', 'ManageBooking ApprovalButtons Init');
				}

				const moduleFactory = await container.get(EXPOSED_MODULE);
				const remoteModule = moduleFactory();
				const RemoteComponent = remoteModule && (remoteModule.default || remoteModule);

				if (typeof RemoteComponent !== 'function') {
					throw new Error(`Remote component is missing for ${REMOTE_NAME}/${EXPOSED_MODULE}`);
				}

				return RemoteComponent;
			})
			.catch((error) => {
				remoteComponentPromise = null;
				throw error;
			});
	}

	return remoteComponentPromise;
};

const ApprovalButtons = (props) => {
	const [RemoteApprovalButtons, setRemoteApprovalButtons] = useState(null);

	useEffect(() => {
		let isMounted = true;

		getRemoteApprovalButtonsComponent()
			.then((remoteComponent) => {
				if (isMounted) {
					setRemoteApprovalButtons(() => remoteComponent);
				}
			})
			.catch((error) => {
				saveServerLogs(error && error.stack ? error.stack : error, 'error', 'ManageBooking ApprovalButtons');
			});

		return () => {
			isMounted = false;
		};
	}, []);

	if (!RemoteApprovalButtons) {
		return null;
	}

	return <RemoteApprovalButtons {...props} />;
};

export default ApprovalButtons;
